import { useState, useCallback, useEffect, useMemo } from 'react'
import { RefreshCw, Download } from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import {
  getFeatureAdoption,
  getFeatureAdoptionTrends,
  getFeatureAdoptionByUser,
  type FeatureUsage,
  type FeatureAdoptionTrend,
  type FeatureUserUsage,
} from '../../../lib/api'
import { useAutoRefresh } from '../../../hooks/useAutoRefresh'
import { CHART_PALETTE } from '../../../lib/chart-colors'
import TrendChart from '../../../components/analytics/TrendChart'
import ChartTooltip from '../../../components/analytics/ChartTooltip'
import PeriodSelector from '../../../components/analytics/PeriodSelector'
import { arrayToCSV, downloadCSV } from '../../../lib/csv-export'
import { AnalyticsNav } from './AnalyticsOverview'
import { formatRelativeTime } from '../../../lib/format'

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export default function FeatureAdoption() {
  const [period, setPeriod] = useState<string>('30d')
  const [features, setFeatures] = useState<FeatureUsage[]>([])
  const [trends, setTrends] = useState<FeatureAdoptionTrend[]>([])
  const [byUser, setByUser] = useState<FeatureUserUsage[]>([])
  // null = "all features" view; string = a specific feature key.
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const [featuresData, trendsData, byUserData] = await Promise.all([
        getFeatureAdoption(period),
        getFeatureAdoptionTrends(period),
        getFeatureAdoptionByUser(period, undefined, 10),
      ])
      setFeatures(featuresData)
      setTrends(trendsData)
      setByUser(byUserData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feature adoption data')
      throw err
    } finally {
      setLoading(false)
    }
  }, [period])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useAutoRefresh(fetchData, {
    baseInterval: 60000,
    maxInterval: 300000,
  })

  function handleExportCSV() {
    if (features.length === 0) return
    const csv = arrayToCSV(features, [
      { key: 'feature', label: 'Feature' },
      { key: 'distinct_users', label: 'Distinct Users' },
      { key: 'event_count', label: 'Total Events' },
    ])
    downloadCSV(csv, `analytics-features-${period}.csv`)
  }

  // Transform flat trends array into pivoted data for multi-line chart
  const trendData = useMemo(() => {
    const byDate: Record<string, Record<string, number | string>> = {}
    for (const t of trends) {
      if (!byDate[t.date]) byDate[t.date] = { date: t.date }
      byDate[t.date][t.feature] = t.event_count
    }
    return Object.values(byDate).sort((a, b) =>
      (a.date as string).localeCompare(b.date as string)
    )
  }, [trends])

  // Build dynamic line config from unique feature names
  const featureNames = useMemo(
    () => [...new Set(trends.map((t) => t.feature))],
    [trends]
  )
  const trendLines = useMemo(
    () =>
      featureNames.map((f, i) => ({
        dataKey: f,
        label: capitalize(f),
        color: CHART_PALETTE[i % CHART_PALETTE.length],
      })),
    [featureNames]
  )

  // Sort features by distinct_users descending for display
  const sortedFeatures = useMemo(
    () => [...features].sort((a, b) => b.distinct_users - a.distinct_users),
    [features]
  )

  // Prepare bar chart data with capitalized names
  const barData = useMemo(
    () =>
      sortedFeatures.map((f) => ({
        ...f,
        displayName: capitalize(f.feature),
      })),
    [sortedFeatures]
  )

  const totalEvents = useMemo(
    () => features.reduce((sum, f) => sum + f.event_count, 0),
    [features]
  )

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-2">Feature Adoption</h1>
          <p className="text-muted-foreground">
            Feature usage patterns and adoption trends across the platform
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSelector value={period} onChange={setPeriod} />
          <button
            onClick={handleExportCSV}
            disabled={features.length === 0}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-secondary transition-colors text-sm disabled:opacity-50"
            title="Download data as CSV"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <AnalyticsNav />

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && features.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : features.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No feature usage data for this period
        </div>
      ) : (
        <div className="space-y-8">
          {/* Feature adoption trend lines chart */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="text-lg font-semibold mb-1">Feature Adoption Trends</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Daily event count per feature area
            </p>
            {trendData.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-muted-foreground">
                No trend data available
              </div>
            ) : (
              <TrendChart data={trendData} lines={trendLines} height={320} />
            )}
          </div>

          {/* Feature usage bar chart */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="text-lg font-semibold mb-4">Users Per Feature Area</h2>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={barData}
                  margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="displayName"
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    width={45}
                    allowDecimals={false}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: 'hsl(var(--muted))', fillOpacity: 0.3 }} />
                  <Bar dataKey="distinct_users" radius={[4, 4, 0, 0]}>
                    {barData.map((_, index) => (
                      <Cell
                        key={index}
                        fill={CHART_PALETTE[index % CHART_PALETTE.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Feature usage table */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="text-lg font-semibold mb-4">Feature Usage Details</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      Feature
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">
                      Distinct Users
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">
                      Total Events
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFeatures.map((f) => (
                    <tr
                      key={f.feature}
                      className="border-b border-border last:border-0"
                    >
                      <td className="py-3 px-4 font-medium">{capitalize(f.feature)}</td>
                      <td className="py-3 px-4 text-right text-muted-foreground tabular-nums">
                        {f.distinct_users}
                      </td>
                      <td className="py-3 px-4 text-right text-muted-foreground tabular-nums">
                        {f.event_count.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Total events tracked: {totalEvents.toLocaleString()}
            </p>
          </div>

          {/* Who's using each feature — top users per feature, filterable */}
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
              <div>
                <h2 className="text-lg font-semibold">Who's Using Each Feature</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Top users per feature area in the selected period.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setSelectedFeature(null)}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    selectedFeature === null
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border hover:bg-secondary'
                  }`}
                >
                  All
                </button>
                {featureNames.map((f) => (
                  <button
                    key={f}
                    onClick={() => setSelectedFeature(f)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      selectedFeature === f
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-border hover:bg-secondary'
                    }`}
                  >
                    {capitalize(f)}
                  </button>
                ))}
              </div>
            </div>
            {byUser.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No per-user usage data for this period.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">User</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Feature</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">What they did</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Events</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Last used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byUser
                      .filter((row) => selectedFeature === null || row.feature === selectedFeature)
                      .slice(0, 50)
                      .map((row) => {
                        const breakdown = row.action_breakdown ?? {}
                        // Sort actions by count desc so the dominant action shows first
                        const breakdownEntries = Object.entries(breakdown).sort(
                          (a, b) => b[1] - a[1],
                        )
                        return (
                          <tr key={`${row.feature}-${row.user_id}`} className="border-b border-border last:border-0">
                            <td className="py-3 px-4">
                              <div className="font-medium">{row.user_name || row.user_email.split('@')[0]}</div>
                              <div className="text-xs text-muted-foreground">{row.user_email}</div>
                            </td>
                            <td className="py-3 px-4 text-sm">{capitalize(row.feature)}</td>
                            <td className="py-3 px-4 text-sm">
                              {breakdownEntries.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {breakdownEntries.map(([action, n]) => (
                                    <span
                                      key={action}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-secondary text-xs"
                                      title={`${action}: ${n}`}
                                    >
                                      <span className="text-muted-foreground">{action}</span>
                                      <span className="font-medium tabular-nums">{n}</span>
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-right text-sm tabular-nums">{row.event_count.toLocaleString()}</td>
                            <td className="py-3 px-4 text-sm text-muted-foreground">
                              {row.last_used_at ? formatRelativeTime(row.last_used_at) : '—'}
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
